import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { platformColor, useChartColors } from "./chartTheme";
import { useChartReveal } from "./motion";
import type { DashboardOverview } from "@/types/api";

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value: number;
    payload?: {
      platform: string;
      count: number;
    };
  }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  const platform = entry.payload?.platform || entry.name || "";
  const count = entry.value;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 p-2.5 shadow-xl backdrop-blur-sm text-xs">
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold text-slate-700 dark:text-slate-200 capitalize">{platform}</span>
        <span className="font-bold tabular-nums text-slate-900 dark:text-white">{count?.toLocaleString()}</span>
      </div>
    </div>
  );
}

export function PlatformDistributionChart({
  data,
}: {
  data: DashboardOverview["platformDistribution"];
}) {
  const colors = useChartColors();
  const { ref, revealed, isAnimationActive } = useChartReveal();
  const safeData = Array.isArray(data) ? data : [];

  return (
    <div ref={ref} className="h-52 md:h-56">
      {revealed && (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={safeData}
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={84}
              paddingAngle={4}
              dataKey="count"
              isAnimationActive={isAnimationActive}
              animationDuration={800}
              animationEasing="ease-out"
              stroke="none"
            >
              {safeData.map((entry, index) => (
                <Cell
                  key={entry.platform}
                  fill={platformColor(entry.platform, colors, index)}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value, entry: any) => (
                <span className="text-xs text-slate-600 dark:text-slate-300">{entry?.payload?.platform ?? value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
